// src/pages/Home.js

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FamilyInsurance from "../assets/Family_Insurnace.png";
import AIZzra from "../assets/AI.png";
import { useLanguage } from "../context/LanguageContext";
import translations from "../data/translations";
import { NETWORKS } from "../constants/networks";
import useOtpFlow from "../hooks/useOtpFlow";
import ZaraChatWidget from "../components/ZaraChatWidget";
import "./Home.css";

const Home = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language] || translations["English"];
  const isEn = language === "English";

  const [phone, setPhone] = useState("");
  const [network, setNetwork] = useState("");
  const [validationError, setValidationError] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  const otp = useOtpFlow();

  const handleSendOtp = async () => {
    if (!phone || phone.length < 10) { setValidationError(t.phoneInvalid || "Please enter a valid mobile number"); return; }
    if (!network) { setValidationError(t.networkInvalid || "Please select a service Provider"); return; }
    setValidationError("");
    try {
      await otp.sendOtp(phone, network);
      setShowOtpModal(true);
      setOtpCode("");
    } catch (err) { /* otp.error already set */ }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 4) return;
    try {
      await otp.verifyOtp(phone, otpCode);
      navigate("/services", { state: { phone, network } });
    } catch (err) { /* otp.error already set */ }
  };

  const handleResend = async () => {
    setOtpCode("");
    await otp.sendOtp(phone, network);
  };

  return (
    <div className="container-fluid px-3 px-md-4" style={{ maxWidth: "1200px", margin: "0 auto" }}>
      <Header />

      {/* HERO */}
      <div className="row align-items-center gy-2">
        <div className="col-12 col-lg-6 hero-content">
          <p className="display-6 fw-bold mb-5">
            {t.heroTitle1} <span style={{ color: "#00B7E8" }}>{t.heroHighlight1}</span>,
            <br />
            {t.heroTitle2} <span style={{ color: "#00B7E8" }}>{t.heroHighlight2}</span> {t.heroTitle3}
          </p>
          <div className="d-flex align-items-center gap-3 p-3 rounded-4 bg-light border shadow-sm">
            <div className="bg-info text-white rounded-circle d-flex justify-content-center align-items-center fw-bold flex-shrink-0" style={{ width: 50, height: 50, fontSize: "1.2rem" }}>✓</div>
            <div>
              <h4 style={{ marginBottom: "5px" }}>{t.dontWorry}</h4>
              <p style={{ color: "#666", fontSize: ".9rem", marginBottom: 0 }}>
                {t.aiDesc.split("AI ZARA")[0]}
                <b style={{ color: "#00B7E8" }}>AI ZARA</b>
                {t.aiDesc.split("AI ZARA")[1]}
              </p>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6 text-center position-relative">
          <div className="d-none d-lg-block position-absolute top-50 start-50" style={{ width: "340px", height: "340px", border: "10px solid rgba(0,183,232,.15)", borderRadius: "30px", transform: "translate(-50%, -50%) rotate(10deg)", zIndex: 0, pointerEvents: "none" }} />
          <img src={FamilyInsurance} alt="family" className="hero-image" />
        </div>
      </div>

      {/* FORM */}
      <div className="px-0 px-md-3 pb-4">
        <div className="card shadow-sm border-0 rounded-4 p-4">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="bg-info text-white rounded-circle d-flex justify-content-center align-items-center flex-shrink-0" style={{ width: 42, height: 42 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v2h20v-2c0-3.3-6.7-5-10-5z" /></svg>
            </div>
            <div>
              <p style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a2e", marginBottom: "2px" }}>{t.letsStart}</p>
              <p style={{ fontSize: "0.8rem", color: "#888", marginBottom: 0 }}>{t.formSub}</p>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6">
              <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, color: "#1a1a2e", marginBottom: "6px" }}>{t.phoneLabel}</label>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)", color: "#bbb" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></svg>
                </span>
                <input className="form-control" type="tel" placeholder={t.phonePlaceholder} value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))}
                  style={{ width: "100%", padding: "10px 12px 10px 34px", border: "1.5px solid #dde", borderRadius: "8px", fontSize: "0.88rem", outline: "none", fontFamily: "inherit" }}
                  onFocus={e => e.target.style.borderColor = "#0099CC"} onBlur={e => e.target.style.borderColor = "#dde"} />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, color: "#1a1a2e", marginBottom: "6px" }}>{t.networkLabel}</label>
              <div style={{ position: "relative" }}>
                <select value={network} onChange={e => setNetwork(e.target.value)}
                  style={{ width: "100%", padding: "10px 34px 10px 12px", border: "1.5px solid #dde", borderRadius: "8px", fontSize: "0.88rem", outline: "none", background: "white", cursor: "pointer", appearance: "none", fontFamily: "inherit", color: network ? "#1a1a2e" : "#aaa" }}
                  onFocus={e => e.target.style.borderColor = "#0099CC"} onBlur={e => e.target.style.borderColor = "#dde"}>
                  <option value="">{t.networkPlaceholder}</option>
                  {NETWORKS.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span style={{ position: "absolute", right: "11px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "#888" }}>▾</span>
              </div>
            </div>
          </div>

          {validationError && <p style={{ color: "#e74c3c", fontSize: "0.78rem", marginBottom: "10px" }}>⚠️ {validationError}</p>}

          <button onClick={handleSendOtp} disabled={otp.sending}
            style={{ width: "100%", padding: "13px", background: otp.sending ? "#77c4e8" : "#0099CC", color: "white", border: "none", borderRadius: "8px", fontSize: "1rem", fontWeight: 600, cursor: otp.sending ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "background 0.2s" }}
            onMouseEnter={e => { if (!otp.sending) e.currentTarget.style.background = "#007aaa"; }}
            onMouseLeave={e => { if (!otp.sending) e.currentTarget.style.background = "#0099CC"; }}>
            {otp.sending ? (t.sendingOtp || "Sending OTP...") : t.nextBtn}
          </button>

          <p style={{ textAlign: "center", fontSize: "0.75rem", color: "#aaa", marginTop: "10px", marginBottom: 0 }}>🔒 {t.safeText}</p>
        </div>
      </div>

      {/* FEATURE BOXES */}
      <div className="row border-top border-bottom">
        {[
          { icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0099CC" strokeWidth="1.8"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.12 1.18 2 2 0 012.11 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.09a16 16 0 006 6l.56-.56a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.9l-.08 2.02z" /></svg>), title: "We're Here to Help", desc: <>Our support team and <span style={{ color: "#0099CC", fontWeight: 600 }}>AI ZARA</span> are available to assist you every step of the way.</> },
          { icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0099CC" strokeWidth="1.8"><path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z" /><path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" /></svg>), title: "Resolve Your Problem", desc: "Raise a claim, complaint or cancel a service – easily and quickly." },
          { icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0099CC" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>), title: "Your Protection, Our Priority", desc: "We believe in real protection and financial inclusion for everyone." },
        ].map((item, i) => (
          <div key={i} className="col-12 col-md-6 col-lg-4 feature-card" style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#e8f6fb", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>{item.icon}</div>
            <div>
              <p style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1a1a2e", marginBottom: "4px" }}>{item.title}</p>
              <p style={{ fontSize: "0.74rem", color: "#666", lineHeight: 1.5, marginBottom: 0 }}>{item.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* AI ZARA — ab reusable widget se */}
      <ZaraChatWidget isEn={isEn}>
        {(openChat) => (
          <div className="row g-0 align-items-center bg-info-subtle py-4 rounded-4">
            <div className="col-12 col-md-3 text-center mb-3 mb-md-0">
              <img src={AIZzra} alt="AI ZARA" className="img-fluid" style={{ maxWidth: "180px" }} />
            </div>
            <div className="col-12 col-md-6 px-3">
              <div className="card shadow-sm border-0 rounded-4 p-4">
                <p style={{ fontWeight: 700, fontSize: "0.95rem", marginBottom: "8px" }}>
                  Hi! I'm your AI <span style={{ color: "#0099CC" }}>ZARA</span> 🤖
                </p>
                <p style={{ fontSize: "0.82rem", color: "#555", lineHeight: 1.6, marginBottom: "12px" }}>
                  I'm here to understand your issue and help you find the best solution.
                </p>
                <span style={{ color: "#0099CC", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer" }} onClick={openChat}>
                  {t.nextBtn}
                </span>
              </div>
            </div>
            <div className="col-12 col-md-3 mt-4 mt-md-0">
              {["Quick & Easy", "Safe & Secure", "24/7 Support", "Trusted by Millions"].map((f, i) => (
                <div key={i} className="d-flex align-items-center gap-2 mb-3">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0099CC" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span style={{ fontSize: "0.85rem" }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </ZaraChatWidget>

      {/* OTP MODAL (top form) */}
      {showOtpModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "grid", placeItems: "center", padding: "20px" }}>
          <div style={{ width: "100%", maxWidth: "440px", borderRadius: "24px", background: "#fff", padding: "28px", boxShadow: "0 22px 60px rgba(0,0,0,0.18)", position: "relative" }}>
            <button onClick={() => setShowOtpModal(false)} style={{ position: "absolute", top: "18px", right: "18px", width: "34px", height: "34px", borderRadius: "50%", border: "none", background: "#f4f4f4", cursor: "pointer", fontSize: "1.15rem", color: "#555" }} aria-label={t.otpClose || "Close"}>×</button>

            <div style={{ marginBottom: "18px" }}>
              <p style={{ fontSize: "1.15rem", fontWeight: 700, marginBottom: "8px", color: "#1a1a2e" }}>{t.otpVerifyTitle || "Verify your phone number"}</p>
              <p style={{ fontSize: "0.9rem", color: "#6b7280", marginBottom: "12px" }}>{t.otpVerifySub || "Enter the OTP sent to your mobile number."}</p>
              <p style={{ fontSize: "0.82rem", color: "#555" }}>{t.otpSentTo || "Sent to"}: <strong>{phone}</strong> • {network}</p>
            </div>

            <input value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder={t.otpPlaceholder || "Enter OTP"} type="tel"
              style={{ width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #dde", fontSize: "0.95rem", outline: "none", fontFamily: "inherit", marginBottom: "18px" }} />

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "18px" }}>
              <div style={{ color: "#1a73e8", fontWeight: 600, fontSize: "0.95rem" }}>{t.otpTimerLabel || "Expires in"}: {otp.formattedTimer}</div>
              <button onClick={handleResend} type="button" disabled={otp.sending} style={{ border: "none", background: "transparent", color: "#0099CC", fontWeight: 700, cursor: otp.sending ? "not-allowed" : "pointer", fontSize: "0.85rem" }}>
                {t.otpResendButton || "Resend OTP"}
              </button>
            </div>

            {otp.error && <p style={{ color: "#842029", background: "#f8d7da", borderRadius: "12px", padding: "12px", marginBottom: "14px", fontSize: "0.88rem" }}>{otp.error}</p>}

            <button onClick={handleVerifyOtp} disabled={otp.verifying || otp.timerSeconds <= 0}
              style={{ width: "100%", padding: "13px", background: otp.verifying || otp.timerSeconds <= 0 ? "#77c4e8" : "#0099CC", color: "white", border: "none", borderRadius: "10px", fontSize: "1rem", fontWeight: 700, cursor: otp.verifying || otp.timerSeconds <= 0 ? "not-allowed" : "pointer" }}>
              {otp.verifying ? (t.verifyingOtp || "Verifying...") : (t.otpVerifyButton || "Verify OTP")}
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Home;